import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../../styles/AdminModal.css';
import '../../styles/AdminBlogModal.css';
import { updateBlogPost, fetchBlogPostDetails } from '../../services/AdminBlogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import BlogPostForm from '../../forms/blog/BlogPostForm';

const BlogPostEditModal = ({ isOpen, onClose, onSuccess, post, categories }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    main_image: null,
    category_id: '',
    status: 'draft',
  });

  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [contentImages, setContentImages] = useState([]);
  const [existingContentImages, setExistingContentImages] = useState([]);
  const formRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      if (!post || !isOpen) return;

      // Si el post no trae content (porque proviene del listado), obtener detalles completos
      let hydratedPost = post;
      if (!('content' in post) || post.content === null || post.content === undefined) {
        try {
          hydratedPost = await fetchBlogPostDetails(post.id);
        } catch (_) {
          // Si falla, continuar con el post original
        }
      }

      // Normalizar content: puede venir como lista, string JSON o string plano
      let normalizedContent = '';
      if (Array.isArray(hydratedPost.content)) {
        normalizedContent = hydratedPost.content;
      } else if (typeof hydratedPost.content === 'string' && hydratedPost.content.trim()) {
        try {
          const parsed = JSON.parse(hydratedPost.content);
          normalizedContent = Array.isArray(parsed) ? parsed : [{ type: 'text', content: hydratedPost.content }];
        } catch {
          normalizedContent = [{ type: 'text', content: hydratedPost.content }];
        }
      } else if (typeof hydratedPost.plain_text_content === 'string' && hydratedPost.plain_text_content.trim()) {
        // Fallback: usar plain_text_content si existe
        normalizedContent = [{ type: 'text', content: hydratedPost.plain_text_content }];
      } else {
        normalizedContent = [];
      }

      // Asignar image_id faltantes usando content_images por orden
      const existingImages = Array.isArray(hydratedPost.content_images) ? hydratedPost.content_images : [];
      let imageCursor = 0;
      const contentWithIds = (normalizedContent || []).map(block => {
        if (block && block.type === 'image') {
          const hasId = block.image_id !== undefined && block.image_id !== null && String(block.image_id).trim() !== '';
          if (!hasId && existingImages[imageCursor] && existingImages[imageCursor].id) {
            const patched = { ...block, image_id: existingImages[imageCursor].id };
            imageCursor += 1;
            return patched;
          }
          imageCursor += 1;
        }
        return block;
      });

      const snapshot = {
        title: hydratedPost.title || '',
        content: contentWithIds,
        main_image: hydratedPost.main_image_url || hydratedPost.main_image || null,
        category_id: hydratedPost.category?.id ? String(hydratedPost.category.id) : '',
        status: hydratedPost.status || 'draft',
      };

      setFormData(snapshot);
      setInitialSnapshot(snapshot);

      setErrors({});
      setContentImages([]);
      setExistingContentImages(existingImages);
    };

    init();
  }, [post, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'main_image') {
      const file = files[0];
      setFormData(prevFormData => ({
        ...prevFormData,
        main_image: file,
      }));
    }
  };

  const handleContentImagesChange = (images) => {
    setContentImages(images);
  };

  // Comparación de archivos por nombre/tamaño/fecha
  const isSameFile = (a, b) => {
    if (!(a instanceof File) || !(b instanceof File)) return false;
    return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified && a.type === b.type;
  };

  // Determinar si hay cambios (dirty)
  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;

    const titleChanged = formData.title !== initialSnapshot.title;
    const catChanged = String(formData.category_id || '') !== String(initialSnapshot.category_id || '');
    const statusChanged = formData.status !== initialSnapshot.status;

    // main_image: URL string o File
    let imageChanged = false;
    const initialImage = initialSnapshot.main_image;
    const currentImage = formData.main_image;
    if (initialImage instanceof File || currentImage instanceof File) {
      imageChanged = !(initialImage && currentImage && isSameFile(initialImage, currentImage));
      // si uno es file y el otro no, considerar cambiado
      if ((initialImage instanceof File) !== (currentImage instanceof File)) imageChanged = true;
    } else {
      imageChanged = String(initialImage || '') !== String(currentImage || '');
    }

    const contentA = Array.isArray(initialSnapshot.content) ? initialSnapshot.content : [];
    const contentB = Array.isArray(formData.content) ? formData.content : [];
    const contentChanged = JSON.stringify(contentA) !== JSON.stringify(contentB);

    // Si hay nuevas imágenes de contenido seleccionadas
    const contentImgsChanged = contentImages && contentImages.length > 0;

    return titleChanged || catChanged || statusChanged || imageChanged || contentChanged || contentImgsChanged;
  }, [formData, initialSnapshot, contentImages]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }

    // Validar contenido - puede ser string o array JSON
    if (Array.isArray(formData.content)) {
      if (formData.content.length === 0) {
        setErrors({ content: 'Content is required' });
        return;
      }
      const hasContent = formData.content.some(block => 
        block.type === 'text' && block.content && block.content.trim()
      );
      if (!hasContent) {
        setErrors({ content: 'Content must have at least some text' });
        return;
      }
    } else if (typeof formData.content === 'string') {
      if (!formData.content.trim()) {
        setErrors({ content: 'Content is required' });
        return;
      }
    } else {
      setErrors({ content: 'Content is required' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);

      let contentToSend = formData.content;
      if (Array.isArray(formData.content)) {
        contentToSend = JSON.stringify(formData.content);
      }
      formDataToSend.append('content', contentToSend);

      formDataToSend.append('category_id', formData.category_id || '');
      formDataToSend.append('status', formData.status);

      if (formData.main_image && formData.main_image instanceof File) {
        formDataToSend.append('main_image', formData.main_image);
      }

      if (contentImages.length > 0) {
        contentImages.forEach((imageData) => {
          if (imageData.file instanceof File) {
            formDataToSend.append('content_images', imageData.file);
          }
        });
      }

      await updateBlogPost(post.id, formDataToSend);
      onSuccess();
    } catch (error) {
      setErrors({ general: 'Failed to update blog post. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !post) return null;

  return (
    <div className="admin__modal">
      <div className="admin__modal-content">
        <div className="admin__modal-intro">
          <h2>Edit Blog Post</h2>
          <button className="admin__modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        {errors.general && (
          <div className="error-message">{errors.general}</div>
        )}

        <div className="admin__modal-body blog">
          <BlogPostForm
            ref={formRef}
            formData={formData}
            errors={errors}
            categories={categories}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            onContentImagesChange={setContentImages}
            existingContentImages={existingContentImages}
          />
        </div>

        <div className="admin__modal-buttons">
          <button className="button secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="button" onClick={handleSave} disabled={loading || !isDirty}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditModal; 